import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import settingsService, { CurrencySettings, UpdateCurrencySettingsRequest } from '../../services/settingsService';
import { DEFAULT_BGN_TO_EUR_RATE } from '../../utils/currency';

interface SettingsState {
  currencySettings: CurrencySettings;
  loading: boolean;
  error: string | null;
}

const initialState: SettingsState = {
  currencySettings: {
    currency_display_mode: 'dual',
    bgn_to_eur_rate: DEFAULT_BGN_TO_EUR_RATE
  },
  loading: false,
  error: null
};

// Async thunks
export const fetchCurrencySettings = createAsyncThunk(
  'settings/fetchCurrencySettings',
  async (_, { rejectWithValue }) => {
    try {
      return await settingsService.getCurrencySettings();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch currency settings');
    }
  }
);

export const updateCurrencySettings = createAsyncThunk(
  'settings/updateCurrencySettings',
  async (settings: UpdateCurrencySettingsRequest, { rejectWithValue }) => {
    try {
      return await settingsService.updateCurrencySettings(settings);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update currency settings');
    }
  }
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch currency settings
    builder
      .addCase(fetchCurrencySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrencySettings.fulfilled, (state, action) => {
        state.loading = false;
        state.currencySettings = action.payload;
      })
      .addCase(fetchCurrencySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update currency settings
    builder
      .addCase(updateCurrencySettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCurrencySettings.fulfilled, (state, action) => {
        state.loading = false;
        state.currencySettings = action.payload;
      })
      .addCase(updateCurrencySettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;